import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Notification, toast } from "@telia/teddy";
import _size from "lodash/size";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { CART_ITEM_TYPE } from "../../../../../constants";
import { RootState } from "../../../../../models";
import { ExtraSimType } from "../../../../../models/common";
import { VasOffering } from "../../../../../models/state";
import { isBroadbandCode, isZeroPlaySubscription } from "../../../../../shared/subscriptionCodeUtils";
import { addExtraSimToCart, addServicesFromRetailerFlowToCart } from "../../../checkout/state/actions/cartActions";
import * as AT from "../../../checkout/state/actionTypes";
import { getItemByBundleId } from "../../../checkout/state/selectors/cartSelectors";
import { getSubscriptions, isEsimSelected } from "../../../checkout/state/selectors/subscriptionsSelectors";
import { isTypeLoading } from "../../../loading/state/selectors";
import { Header } from "../../components/atoms/Header";
import { SubscriptionAdditionalServices } from "../../components/molecules/SubscriptionAdditionalServices";
import { SimChoice, SimConfig } from "../../components/molecules/RetailerSubscriptionSelectable";
import { SubscriptionDropdown } from "../../components/molecules/SubscriptionDropdown";
import { getCustomerExistingAccounts } from "../../state/selectors/customerSelectors";
import { getAccountId } from "../../../checkout/state/selectors/accountSelectors";
import "./AdditionalServices.less";

interface AdditionalServicesNotification {
    variant: "gray" | "information";
    iconName?: "alert-filled";
    message: string;
}

const getNotificationConfig = (
    hasSubscriptions: boolean,
    hasEligibleSubscriptions: boolean,
): AdditionalServicesNotification | null => {
    if (!hasSubscriptions) {
        return {
            variant: "gray",
            iconName: "alert-filled",
            message:
                "Ingen abonnement i handlekurven. Legg til et abonnement først for å kunne velge tilleggstjenester.",
        };
    }
    if (!hasEligibleSubscriptions) {
        return {
            variant: "information",
            message: "Ingen abonnement i handlekurven som støtter tilleggstjenester.",
        };
    }
    return null;
};

const AdditionalServices = () => {
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const preselectPhoneNumber = searchParams.get("phoneNumber") || undefined;
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string | undefined>(undefined);
    const [selectedPhoneNumber, setSelectedPhoneNumber] = useState("");
    const [isExistingSub, setIsExistingSub] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const errorRef = useRef<HTMLDivElement>(null);
    const subscriptions = useSelector(getSubscriptions);
    const selectedBan = useSelector(getAccountId);
    const existingSubscriptions = useSelector((state: RootState) => {
        const accounts = getCustomerExistingAccounts(state);
        if (selectedBan) {
            const current = accounts.find((a) => a.accountId === selectedBan);
            return current?.subscriptions || [];
        }
        return accounts.flatMap((account) => account.subscriptions || []);
    }, shallowEqual);
    const hasExistingEligibleSubscriptions = useMemo(
        () =>
            existingSubscriptions.some(
                (sub) => !isBroadbandCode(sub.offeringCode) && !isZeroPlaySubscription(sub.offeringCode),
            ),
        [existingSubscriptions],
    );
    const hasSubscriptions = _size(subscriptions) > 0 || hasExistingEligibleSubscriptions;
    const hasAdditionalServicesEligibleSubscriptions = useMemo(
        () =>
            Object.values(subscriptions).some((sub) => !isBroadbandCode(sub.code)) || hasExistingEligibleSubscriptions,
        [subscriptions, hasExistingEligibleSubscriptions],
    );
    const notificationConfig = getNotificationConfig(hasSubscriptions, hasAdditionalServicesEligibleSubscriptions);
    const isAddingSimToCart = useSelector((state: RootState) =>
        isTypeLoading(state, AT.CHECKOUT_ADD_EXTRA_SIM_TO_CART),
    );
    const isAddingServicesToCart = useSelector((state: RootState) =>
        isTypeLoading(state, AT.CHECKOUT_ADD_SERVICE_TO_CART),
    );
    const isRemovingServiceFromCart = useSelector((state: RootState) =>
        isTypeLoading(state, AT.CHECKOUT_REMOVE_SERVICE_FROM_CART),
    );
    const isLoading = isAddingSimToCart || isAddingServicesToCart || isRemovingServiceFromCart;
    const subscriptionIsEsim = useSelector((state: RootState) =>
        selectedSubscriptionId ? isEsimSelected(state, selectedSubscriptionId) : false,
    );

    const selectedBundleId = useMemo(() => {
        if (!isExistingSub) return selectedSubscriptionId;
        const normalizedPhoneNumber = selectedPhoneNumber.replace(/\s/g, "");
        return Object.values(subscriptions).find(
            (subscription) =>
                subscription.phoneNumber?.selectedPhoneNumber.replace(/\s/g, "") === normalizedPhoneNumber,
        )?.id;
    }, [isExistingSub, selectedSubscriptionId, selectedPhoneNumber, subscriptions]);
    const selectedHandset = useSelector((state: RootState) =>
        getItemByBundleId(state, selectedBundleId).find(
            (item) => item.type === CART_ITEM_TYPE.HANDSET || item.type === CART_ITEM_TYPE.HANDSET_PREOWNED,
        ),
    );

    useEffect(() => {
        if (errorMessage) {
            errorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [errorMessage]);

    const handleSubscriptionChange = (subscriptionId: string, phoneNumber: string, isExisting: boolean) => {
        setErrorMessage(null);
        setSelectedSubscriptionId(subscriptionId);
        setSelectedPhoneNumber(phoneNumber);
        setIsExistingSub(isExisting);
    };

    const handleAddServiceToCart = async (service: VasOffering, simConfig?: SimConfig) => {
        setErrorMessage(null);
        if (!selectedSubscriptionId) return;

        try {
            if (service.simType) {
                const isEsim = simConfig?.simChoice === SimChoice.ESim;
                const isPhysicalSim = simConfig?.simChoice === SimChoice.Physical && !!simConfig?.simNumber;
                const simNumber = isPhysicalSim ? simConfig?.simNumber : undefined;
                const normalizedPhoneNumber = selectedPhoneNumber.replace(/\s/g, "");
                const existingCartItemId = Object.values(subscriptions).find(
                    (subscription) =>
                        subscription.phoneNumber?.selectedPhoneNumber.replace(/\s/g, "") === normalizedPhoneNumber,
                )?.id;

                await dispatch(
                    addExtraSimToCart(
                        service.simType as ExtraSimType,
                        existingCartItemId || "",
                        isEsim ?? false,
                        selectedPhoneNumber,
                        false,
                        undefined,
                        isPhysicalSim,
                        simNumber,
                    ),
                );
                const simLabel =
                    service.simType === "TVILLINGSIM"
                        ? "Ekstra tvillingSIM"
                        : service.simType === "TVILLINGSIM_KLOKKE"
                          ? "Ekstra klokke tvillingSIM"
                          : "Ekstra dataSIM";
                toast.success(`${simLabel} lagt til i handlekurv`, {
                    position: "top-center",
                });
            } else {
                if (service.code.includes("INSURANCE") && !selectedHandset?.imei) {
                    setErrorMessage("IMEI må legges inn på utstyr før forsikring kan legges til.");

                    return;
                }
                await dispatch(
                    addServicesFromRetailerFlowToCart(
                        isExistingSub
                            ? { serviceCode: service.code, phoneNumber: selectedPhoneNumber }
                            : { serviceCode: service.code, cartItemId: selectedSubscriptionId },
                    ),
                );
                toast.success(`${service.name} lagt til`, {
                    position: "top-center",
                });
            }
        } catch (error) {
            setErrorMessage("Det oppsto et problem da tjenester skulle legges til i handlekurven.");
        }
    };

    return (
        <div className="retailer-dashboard-wrapper">
            <Header text="Tilleggstjenester" />
            <div className="retailer-main-section">
                {notificationConfig ? (
                    <Notification type="tile" variant={notificationConfig.variant}>
                        {notificationConfig.iconName ? (
                            <Notification.Icon name={notificationConfig.iconName} />
                        ) : (
                            <Notification.Icon />
                        )}
                        <Notification.Text>{notificationConfig.message}</Notification.Text>
                    </Notification>
                ) : (
                    <>
                        <Box mb="400">
                            <SubscriptionDropdown
                                selectedSubscriptionId={selectedSubscriptionId}
                                onSubscriptionChange={handleSubscriptionChange}
                                preselectPhoneNumber={preselectPhoneNumber}
                                variant="additionalServices"
                                shouldIncludeExistingSubscriptions
                            />
                        </Box>
                        {selectedSubscriptionId && (
                            <>
                                <SubscriptionAdditionalServices
                                    isLoading={isLoading}
                                    subscriptionId={selectedSubscriptionId}
                                    phoneNumber={selectedPhoneNumber}
                                    isExisting={isExistingSub}
                                    handleAddServiceToCart={handleAddServiceToCart}
                                    subscriptionIsEsim={subscriptionIsEsim}
                                />

                                {errorMessage && (
                                    <div className="additional-services-error" ref={errorRef}>
                                        <Notification type="tile" variant="error" mb="300">
                                            <Notification.Icon />
                                            <Notification.Text>{errorMessage}</Notification.Text>
                                        </Notification>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default AdditionalServices;
